import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, ArrowLeft, Zap, Shield, Clock, Headphones } from 'lucide-react';

const plans = [
  {
    id: 'avulso',
    name: 'Avulso',
    description: 'Pague por acionamento',
    pricePerMission: 34.90,
    monthlyPrice: 34.90,
    missionLimit: '1',
    highlighted: false,
    features: [
      '1 acionamento avulso',
      'R$ 34,90 por acionamento',
      'Acesso à plataforma',
      'Relatórios completos',
      'Dashboard operacional',
      'Dashboard financeiro',
      'Múltiplos usuários',
      'Suporte dedicado',
      'Treinamento incluído'
    ]
  },
  {
    id: 'starter',
    name: 'Cliente Starter',
    description: 'Para pequenas operações',
    pricePerMission: 32.90,
    monthlyPrice: 658.00,
    missionLimit: '20',
    highlighted: false,
    features: [
      'Até 20 acionamentos/mês',
      'R$ 32,90 por acionamento',
      'Acesso à plataforma',
      'Relatórios completos',
      'Dashboard operacional',
      'Dashboard financeiro',
      'Múltiplos usuários',
      'Suporte dedicado',
      'Treinamento incluído'
    ]
  },
  {
    id: 'small',
    name: 'Cliente Small',
    description: 'Para operações em crescimento',
    pricePerMission: 27.90,
    monthlyPrice: 1395.00,
    missionLimit: '50',
    highlighted: false,
    features: [
      'Até 50 acionamentos/mês',
      'R$ 27,90 por acionamento',
      'Acesso à plataforma',
      'Relatórios completos',
      'Dashboard operacional',
      'Dashboard financeiro',
      'Múltiplos usuários',
      'Suporte dedicado',
      'Treinamento incluído'
    ]
  },
  {
    id: 'medium',
    name: 'Cliente Medium',
    description: 'Para operações médias',
    pricePerMission: 22.90,
    monthlyPrice: 2290.00,
    missionLimit: '100',
    highlighted: true,
    features: [
      'Até 100 acionamentos/mês',
      'R$ 22,90 por acionamento',
      'Acesso à plataforma',
      'Relatórios completos',
      'Dashboard operacional',
      'Dashboard financeiro',
      'Múltiplos usuários',
      'Suporte dedicado',
      'Treinamento incluído'
    ]
  },
  {
    id: 'large',
    name: 'Cliente Large',
    description: 'Para grandes operações',
    pricePerMission: 17.90,
    monthlyPrice: 3580.00,
    missionLimit: '200',
    highlighted: false,
    features: [
      'Até 200 acionamentos/mês',
      'R$ 17,90 por acionamento',
      'Acesso à plataforma',
      'Relatórios completos',
      'Dashboard operacional',
      'Dashboard financeiro',
      'Múltiplos usuários',
      'Suporte dedicado',
      'Treinamento incluído'
    ]
  },
  {
    id: 'enterprise',
    name: 'Cliente Enterprise',
    description: 'Para operações de alta escala',
    pricePerMission: null,
    monthlyPrice: null,
    missionLimit: '>200',
    highlighted: false,
    features: [
      'Acima de 200 acionamentos/mês',
      'Preço sob consulta',
      'Acesso à plataforma',
      'Relatórios completos',
      'Dashboard operacional',
      'Dashboard financeiro',
      'Múltiplos usuários',
      'Suporte dedicado',
      'Treinamento incluído'
    ]
  }
];

const PlansPage = () => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link to="/login" className="inline-flex items-center text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao login
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <h1 className="text-5xl font-semibold text-slate-900 tracking-tight mb-6" style={{ fontFamily: 'Public Sans' }}>
            Planos e Preços
          </h1>
          <p className="text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed">
            Escolha o plano ideal para sua operação de pronta resposta no transporte rodoviário
          </p>
          <div className="flex justify-center gap-12 mt-12">
            <div className="flex items-center gap-3 text-slate-600">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <Shield className="w-5 h-5 text-slate-600" />
              </div>
              <span className="font-medium">Segurança garantida</span>
            </div>
            <div className="flex items-center gap-3 text-slate-600">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-slate-600" />
              </div>
              <span className="font-medium">Resposta 24/7</span>
            </div>
            <div className="flex items-center gap-3 text-slate-600">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <Headphones className="w-5 h-5 text-slate-600" />
              </div>
              <span className="font-medium">Suporte especializado</span>
            </div>
          </div>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card 
              key={plan.id} 
              className={`relative flex flex-col rounded-2xl border-0 ${
                plan.highlighted 
                  ? 'bg-slate-900 text-white shadow-2xl scale-105' 
                  : 'bg-white shadow-sm hover:shadow-lg'
              } transition-all duration-300`}
              data-testid={`plan-card-${plan.id}`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-white text-slate-900 px-4 py-1 rounded-full font-medium">Mais Popular</Badge>
                </div>
              )}
              <CardHeader className="pb-2">
                <CardTitle className={`text-xl ${plan.highlighted ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Public Sans' }}>
                  {plan.name}
                </CardTitle>
                <CardDescription className={plan.highlighted ? 'text-slate-300' : 'text-slate-500'}>
                  {plan.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="mb-6">
                  {plan.monthlyPrice ? (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className={`text-4xl font-semibold ${plan.highlighted ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'JetBrains Mono' }}>
                          {formatCurrency(plan.monthlyPrice)}
                        </span>
                        <span className={plan.highlighted ? 'text-slate-400' : 'text-slate-500'}>
                          {plan.id === 'avulso' ? '/acionamento' : '/mês'}
                        </span>
                      </div>
                      {plan.id !== 'avulso' && (
                        <p className={`text-sm mt-2 ${plan.highlighted ? 'text-slate-400' : 'text-slate-500'}`}>
                          {formatCurrency(plan.pricePerMission)} por acionamento · {plan.missionLimit} acionamentos
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className={`text-3xl font-semibold ${plan.highlighted ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Public Sans' }}>
                        Sob Consulta
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        plan.highlighted ? 'bg-white/10' : 'bg-slate-100'
                      }`}>
                        <Check className={`w-3 h-3 ${plan.highlighted ? 'text-white' : 'text-slate-600'}`} />
                      </div>
                      <span className={`text-sm ${plan.highlighted ? 'text-slate-300' : 'text-slate-600'}`}>
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  className={`w-full h-12 rounded-xl font-medium ${
                    plan.highlighted 
                      ? 'bg-white text-slate-900 hover:bg-slate-100' 
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  } btn-premium`}
                  data-testid={`select-plan-${plan.id}`}
                >
                  {plan.id === 'avulso' ? 'Comprar Avulso' : plan.id === 'enterprise' ? 'Falar com Vendas' : 'Assinar Plano'}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Notice */}
        <div className="mt-16 text-center">
          <Card className="max-w-2xl mx-auto premium-card border-0">
            <CardContent className="p-8">
              <div className="flex items-center justify-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-slate-600" />
                </div>
                <span className="font-semibold text-slate-900">Pagamentos em breve</span>
              </div>
              <p className="text-slate-500">
                Os pagamentos serão habilitados em uma segunda etapa do projeto. 
                Por enquanto, entre em contato para contratar um plano.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* FAQ or Contact */}
        <div className="mt-20 text-center">
          <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Public Sans' }}>
            Dúvidas sobre qual plano escolher?
          </h2>
          <p className="text-slate-500 mb-8">
            Nossa equipe está pronta para ajudar você a encontrar a melhor solução para sua operação.
          </p>
          <Button 
            variant="outline" 
            size="lg" 
            className="rounded-xl h-12 px-8 border-slate-200 hover:bg-slate-50"
            data-testid="contact-sales-btn"
          >
            Falar com vendas
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-slate-500">
          <p className="font-medium text-slate-900 mb-2">PR APP - Sistema de Pronta Resposta no Transporte Rodoviário</p>
          <p>Todos os preços em Reais (BRL). Valores sujeitos a alteração.</p>
        </div>
      </footer>
    </div>
  );
};

export default PlansPage;
